// Catalogue list logic shared by the Partner Hub and Ops product pages: filters, row labels,
// stock display, quick stock changes, the delete dialog's wording and the audit history.
// Pure: no DOM, no Intl. The server is authoritative; this only shapes what people see.

import { peso } from '../../lib/format.js';

/** @typedef {import('@popcart/core/v1').AdminProduct} AdminProduct */
/** @typedef {import('@popcart/core/v1').AdminProductDetail} AdminProductDetail */
/** @typedef {import('@popcart/core/v1').ProductStatus} ProductStatus */
/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */
/** @typedef {import('@popcart/core/v1').ProductAuditEntry} ProductAuditEntry */

export const LINES = /** @type {const} */ (['fresh', 'grill', 'mart']);
export const LINE_LABEL = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' }; // B3.10: labels say Grocery, data keeps mart

export const STATUSES = /** @type {const} */ (['active', 'hidden', 'archived']);
export const STATUS_LABEL = { active: 'Active', hidden: 'Hidden', archived: 'Archived' };
/** @type {Record<ProductStatus, 'success' | 'neutral' | 'warning'>} */
export const STATUS_TONE = { active: 'success', hidden: 'neutral', archived: 'warning' };

/** Search terms go to the server trimmed and at most 60 characters (contract: Lists). */
export const MAX_Q = 60;

/**
 * Read the list filters from the URL (unknown values fall back to the defaults).
 * @param {{ get(k: string): string | null }} params
 * @param {readonly string[]} [lines] the lines this scope can show (a shop's own lines)
 * @returns {{ q: string; line: ProductLine | 'all'; status: ProductStatus; shopId: string }}
 */
export function readFilters(params, lines = LINES) {
  const line = params.get('line');
  const status = params.get('status');
  return {
    q: (params.get('q') || '').slice(0, MAX_Q),
    line: line && lines.includes(line) ? /** @type {ProductLine} */ (line) : 'all',
    status: status && /** @type {readonly string[]} */ (STATUSES).includes(status) ? /** @type {ProductStatus} */ (status) : 'active',
    shopId: params.get('shop') || '',
  };
}

/**
 * The query the list sends (`q` trimmed, `all` lines dropped).
 * @param {{ q: string; line: ProductLine | 'all'; status: ProductStatus; shopId?: string }} f
 */
export function listParams(f) {
  /** @type {{ q?: string; line?: ProductLine; status: ProductStatus; shopId?: string }} */
  const out = { status: f.status };
  const q = f.q.trim().slice(0, MAX_Q);
  if (q) out.q = q;
  if (f.line !== 'all') out.line = f.line;
  if (f.shopId) out.shopId = f.shopId;
  return out;
}

/** Line tabs: "All" then the lines the shop sells, in a fixed order. @param {readonly string[]} shopLines */
export function lineTabs(shopLines) {
  const own = LINES.filter((l) => shopLines.includes(l));
  return [{ value: 'all', label: 'All' }, ...own.map((l) => ({ value: l, label: LINE_LABEL[l] }))];
}

/**
 * Does a product (after a local change) still belong in the current filter? Used to drop a row
 * the moment it's hidden from the Active tab, before the refetch lands.
 * @param {Pick<AdminProduct, 'status' | 'line' | 'name' | 'shopId'>} p
 * @param {{ q: string; line: ProductLine | 'all'; status: ProductStatus; shopId?: string }} f
 */
export function matchesFilters(p, f) {
  if (p.status !== f.status) return false;
  if (f.line !== 'all' && p.line !== f.line) return false;
  if (f.shopId && p.shopId !== f.shopId) return false;
  const q = f.q.trim().toLowerCase();
  return !q || p.name.toLowerCase().includes(q);
}

/** "₱120 / pc", "₱480/kg", "₱89". @param {Pick<AdminProduct, 'pricing' | 'priceCentavos' | 'pricePerKgCentavos' | 'unitLabel'>} p */
export function priceLabel(p) {
  if (p.pricing === 'weight') return `${peso(p.pricePerKgCentavos)}/kg`;
  return p.unitLabel ? `${peso(p.priceCentavos)} / ${p.unitLabel}` : peso(p.priceCentavos);
}

/**
 * Grams as people say them: 750 → "750 g", 1500 → "1.5 kg", 2000 → "2 kg", 1255 → "1.255 kg".
 * @param {number} g
 */
export function gramsLabel(g) {
  if (!Number.isFinite(g)) return '—';
  const neg = g < 0;
  const a = Math.abs(Math.round(g));
  if (a < 1000) return `${neg ? '−' : ''}${a} g`;
  const kg = Math.floor(a / 1000);
  const rest = a % 1000;
  const dec = rest ? '.' + String(rest).padStart(3, '0').replace(/0+$/, '') : '';
  return `${neg ? '−' : ''}${kg}${dec} kg`;
}

/** Stock in the product's own unit. @param {number} n @param {'piece' | 'gram'} unit */
export function qtyLabel(n, unit) {
  return unit === 'gram' ? gramsLabel(n) : `${n < 0 ? '−' : ''}${Math.abs(n)}`;
}

/** Low means 5 pieces or 2 kg or less available. */
export const LOW_PIECES = 5;
export const LOW_GRAMS = 2000;

/** @param {Pick<AdminProduct, 'available' | 'stockUnit'>} p @returns {'sold_out' | 'low' | 'ok'} */
export function stockState(p) {
  if (p.available <= 0) return 'sold_out';
  if (p.available <= (p.stockUnit === 'gram' ? LOW_GRAMS : LOW_PIECES)) return 'low';
  return 'ok';
}

/** "12 available · 14 on hand" / "1.5 kg available". @param {Pick<AdminProduct, 'available' | 'onHand' | 'stockUnit'>} p */
export function stockLabel(p) {
  const a = qtyLabel(p.available, p.stockUnit);
  return p.onHand !== p.available ? `${a} available · ${qtyLabel(p.onHand, p.stockUnit)} on hand` : `${a} available`;
}

/** One tap of the quick stock stepper: a piece, or half a kilo. @param {'piece' | 'gram'} unit */
export function stockStep(unit) {
  return unit === 'gram' ? 500 : 1;
}

/** The stock endpoint takes at most this many units (pieces or grams) per change (v1/shop.js). */
export const MAX_STOCK_DELTA = 10000;

/**
 * Check a stock change before it's sent. Restock only adds; a correction may remove, but never
 * below zero on hand, and needs a note saying why (spoiled, miscounted).
 * @param {{ delta: number; reason: 'restock' | 'adjust'; note?: string; onHand: number; unit: 'piece' | 'gram' }} c
 * @returns {{ ok: true; delta: number; reason: 'restock' | 'adjust'; note?: string } | { ok: false; error: string }}
 */
export function validateStockChange(c) {
  if (!Number.isInteger(c.delta) || c.delta === 0) return { ok: false, error: 'Choose how much to add or remove.' };
  if (Math.abs(c.delta) > MAX_STOCK_DELTA) return { ok: false, error: `Change at most ${qtyLabel(MAX_STOCK_DELTA, c.unit)} at a time.` };
  if (c.reason !== 'restock' && c.reason !== 'adjust') return { ok: false, error: 'Pick a reason.' };
  if (c.reason === 'restock' && c.delta < 0) return { ok: false, error: 'A restock adds stock. Use "Correction" to remove.' };
  if (c.onHand + c.delta < 0) return { ok: false, error: `Only ${qtyLabel(c.onHand, c.unit)} on hand. You can remove at most that.` };
  const note = (c.note || '').trim();
  if (c.delta < 0 && !note) return { ok: false, error: 'Say why stock is being removed (for example: spoiled).' };
  if (note.length > 200) return { ok: false, error: 'Keep the note under 200 characters.' };
  return note ? { ok: true, delta: c.delta, reason: c.reason, note } : { ok: true, delta: c.delta, reason: c.reason };
}

/** "+3", "−1.5 kg", "0". @param {number} delta @param {'piece' | 'gram'} unit */
export function deltaLabel(delta, unit) {
  if (!delta) return '0';
  return (delta > 0 ? '+' : '−') + qtyLabel(Math.abs(delta), unit);
}

/** Combos have no stock of their own: it comes from their components. @param {Pick<AdminProduct, 'isCombo'>} p */
export function canAdjustStock(p) {
  return !p.isCombo;
}

// ── Delete / archive ──────────────────────────────────────────────────────────────────────

/**
 * The confirm dialog tells the truth about what DELETE will do (contract: ordered → archive).
 * @param {Pick<AdminProductDetail, 'name' | 'everOrdered'>} p
 * @returns {{ title: string; body: string; confirm: string; kind: 'archive' | 'delete' }}
 */
export function deleteCopy(p) {
  if (p.everOrdered)
    return {
      kind: 'archive',
      title: `Archive ${p.name}?`,
      body: 'Archive: it has past orders, so it is kept for their receipts. Customers won’t see it and it can’t be ordered. You can restore it from the Archived tab.',
      confirm: 'Archive product',
    };
  return {
    kind: 'delete',
    title: `Delete ${p.name} permanently?`,
    body: 'Delete permanently: it has never been ordered, so it is removed with its stock, photo and options. This can’t be undone.',
    confirm: 'Delete permanently',
  };
}

/** Toast after DELETE, from what the server actually did. @param {'archived' | 'deleted'} result @param {string} name */
export function deleteOutcome(result, name) {
  return result === 'archived' ? `${name} is archived. Restore it any time from Archived.` : `${name} is deleted.`;
}

/**
 * A 409 on delete/archive means an active combo uses it; the server names the combo.
 * @param {{ status?: number; message?: string } | null | undefined} err
 */
export function deleteProblem(err) {
  if (err && err.status === 409)
    return err.message && err.message !== 'Request failed (409)'
      ? err.message
      : 'An active combo uses this product. Remove it from the combo (or hide the combo) first.';
  return null;
}

/** @param {Pick<AdminProduct, 'name'>} p @param {boolean} visible */
export function visibilityToast(p, visible) {
  return visible ? `${p.name} is back on sale.` : `${p.name} is hidden. Customers can’t see or order it.`;
}

/** After restore the product comes back hidden (contract). @param {Pick<AdminProduct, 'name'>} p */
export function restoreToast(p) {
  return `${p.name} is restored as hidden. Show it when it’s ready to sell.`;
}

// ── Cached-list updates ───────────────────────────────────────────────────────────────────

/**
 * Patch one product inside every page of an infinite list (TanStack `{ pages: Page[] }`).
 * @template {{ id: string }} T
 * @param {{ pages: { items: T[]; nextCursor: string | null }[]; pageParams: unknown[] } | undefined} data
 * @param {string} id
 * @param {(p: T) => T | null} fn return null to drop the row
 */
export function patchPages(data, id, fn) {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((pg) => ({
      ...pg,
      items: pg.items.flatMap((p) => {
        if (p.id !== id) return [p];
        const n = fn(p);
        return n ? [n] : [];
      }),
    })),
  };
}

// ── Audit history ─────────────────────────────────────────────────────────────────────────

export const ACTION_LABEL = {
  create: 'Created',
  update: 'Edited',
  hide: 'Hidden',
  show: 'Shown',
  archive: 'Archived',
  restore: 'Restored',
  delete: 'Deleted',
  image: 'Photo changed',
};

const FIELD_LABEL = {
  name: 'Name',
  priceCentavos: 'Price',
  pricePerKgCentavos: 'Price per kg',
  unitLabel: 'Unit',
  description: 'Description',
  categoryId: 'Category',
  category: 'Category',
  source: 'Source',
  sort: 'Sort',
  prepMinutes: 'Prep time',
  barcode: 'Barcode',
  shelf: 'Shelf',
  visible: 'Visible',
  isActive: 'Visible',
  pricing: 'Pricing',
  stockUnit: 'Stock unit',
  menuSection: 'Menu section',
  modifierGroups: 'Choices',
  comboComponents: 'Combo items',
  bands: 'Size bands',
  prepOptions: 'Prep options',
  imageUrl: 'Photo',
  photoUrl: 'Photo',
  initialStock: 'Opening stock',
  onHand: 'On hand',
};

/** @param {string} key @param {unknown} v @returns {string} */
export function auditValue(key, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') {
    if (/Centavos$/.test(key)) return peso(v);
    if (key === 'prepMinutes') return `${v} min`;
    return String(v);
  }
  if (typeof v === 'string') return v.length > 60 ? v.slice(0, 59) + '…' : v;
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length === 1 ? '' : 's'}` : 'none';
  return 'changed';
}

/**
 * One readable line per changed field: { label: 'Price', from: '₱80', to: '₱85' }.
 * @param {Pick<ProductAuditEntry, 'changes'>} e
 */
export function auditChanges(e) {
  return Object.entries(e.changes || {}).map(([k, pair]) => {
    const [from, to] = Array.isArray(pair) ? pair : [undefined, pair];
    return { key: k, label: /** @type {Record<string, string>} */ (FIELD_LABEL)[k] || humanise(k), from: auditValue(k, from), to: auditValue(k, to) };
  });
}

/** camelCase → "Camel case". @param {string} k */
function humanise(k) {
  const s = k.replace(/Centavos$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "Maria · shop owner" / "PopCart Ops". @param {Pick<ProductAuditEntry, 'actorName' | 'actorRole'>} e */
export function auditActor(e) {
  const role = e.actorRole === 'platform_admin' ? 'PopCart Ops' : e.actorRole ? e.actorRole.replace(/_/g, ' ') : null;
  if (e.actorName && role) return `${e.actorName} · ${role}`;
  return e.actorName || role || 'System';
}

// ── Photos ────────────────────────────────────────────────────────────────────────────────

/** The API takes jpeg/png/webp up to 5 MB (contract: Images). */
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

/**
 * Why a picked file can't be used as-is, or null. `converted` is true once the browser has
 * re-encoded it (any image the browser can read becomes a JPEG/WebP first).
 * @param {{ type: string; size: number }} f @param {{ converted?: boolean }} [o]
 */
export function photoProblem(f, o = {}) {
  if (!f.type.startsWith('image/')) return 'Choose a photo (JPEG, PNG or WebP).';
  if (o.converted && !PHOTO_TYPES.includes(f.type)) return 'This photo can’t be used. Save it as JPEG, PNG or WebP.';
  if (o.converted && f.size > MAX_PHOTO_BYTES) return 'This photo is over 5 MB even after shrinking. Use a smaller one.';
  return null;
}

/**
 * Scale a photo to fit a square box, keeping its shape.
 * @param {number} w @param {number} h @param {number} max
 */
export function fitWithin(w, h, max) {
  if (w <= max && h <= max) return { w, h };
  const k = max / Math.max(w, h);
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}
